import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, DollarSign, Sun, Moon } from 'lucide-react';
import BuilderContentRenderer from '../components/BuilderContentRenderer';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to fix relative image URLs in HTML content
const fixImageUrls = (htmlContent) => {
  if (!htmlContent) return '';
  // Convert relative /api/public/uploads/ or /api/uploads/ paths to absolute URLs
  // Also handle any old preview URLs or legacy domains that might be stored
  return htmlContent
    // Relative paths
    .replace(/src=["']\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Preview domain URLs
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Legacy emergent.host domain URLs
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`);
};

const TemplatePreview = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(0);
  const [isDark, setIsDark] = useState(false); // Default to light mode for professional appearance

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates/${templateId}`, { credentials: 'include' });
      if (response.ok) {
        setTemplate(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch template:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#A2FF00] text-xl">Loading preview...</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Template not found</h1>
          <button onClick={() => navigate('/proposals/templates')} className="text-[#A2FF00] hover:underline">
            Back to templates
          </button>
        </div>
      </div>
    );
  }

  const currentSection = template.sections?.[activeSection];

  // Replace placeholders with sample data
  const replacePlaceholders = (content) => {
    if (!content) return '';
    return content
      .replace(/{company_name}/g, 'Acme Corporation')
      .replace(/{main_contact_name}/g, 'John Smith')
      .replace(/{main_contact_first_name}/g, 'John')
      .replace(/{proposal_date}/g, new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }))
      .replace(/{proposal_expiry}/g, new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border-b sticky top-0 z-10`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/proposals/templates/${templateId}/edit`)}
              className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#A2FF00] rounded-sm flex items-center justify-center">
                <span className="text-black font-bold text-lg">Q</span>
              </div>
              <div>
                <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-bold`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Template Preview
                </span>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{template.name}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-full ${isDark ? 'bg-white/10 text-yellow-400' : 'bg-gray-200 text-gray-600'}`}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <span className={`px-3 py-1 ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'} text-sm rounded`}>
              Preview Mode
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 sticky top-24`}>
              <h3 className={`text-xs font-bold ${isDark ? 'text-gray-500' : 'text-gray-500'} uppercase mb-4`}>Sections</h3>
              <nav className="space-y-1">
                {template.sections?.map((section, index) => (
                  <button
                    key={section.section_id}
                    onClick={() => setActiveSection(index)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                      activeSection === index
                        ? 'bg-[#A2FF00]/20 text-[#A2FF00]'
                        : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>

              {/* Default Products */}
              {template.default_products?.length > 0 && (
                <div className={`mt-6 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <h4 className={`text-xs font-bold ${isDark ? 'text-gray-500' : 'text-gray-500'} uppercase mb-2`}>Default Products</h4>
                  <div className="space-y-2">
                    {template.default_products.map((dp, idx) => (
                      <div key={idx} className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        • {dp.product_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Cover Info */}
            <div className={`${isDark ? 'bg-gradient-to-r from-[#A2FF00]/10 to-transparent' : 'bg-gradient-to-r from-green-50 to-transparent'} border-l-4 border-[#A2FF00] p-6 mb-8`}>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Membership Proposal
              </h1>
              <p className="text-xl text-[#A2FF00]">Acme Corporation</p>
              <div className={`flex items-center gap-6 mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <span>Prepared for: John Smith</span>
                <span>Date: {new Date().toLocaleDateString('en-GB')}</span>
              </div>
            </div>

            {/* Section Content */}
            {currentSection && (
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 mb-8`}>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {currentSection.title}
                </h2>
                
                {currentSection.section_type === 'pricing' ? (
                  <div className="space-y-6">
                    <div 
                      className={`prose max-w-none template-content ${isDark ? 'prose-invert' : 'prose-gray light-mode-content'}`}
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHTML(fixImageUrls(replacePlaceholders(currentSection.content)
                          .replace(/<p><em>Note: The pricing table will be displayed below this text in the proposal viewer\.<\/em><\/p>/gi, '')
                          .replace(/Note: The pricing table will be displayed below this text in the proposal viewer\./gi, '')))
                      }}
                    />
                    
                    {/* Sample Pricing Display */}
                    <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-6`}>
                      <div className="flex items-center gap-2 mb-4">
                        <DollarSign className="w-5 h-5 text-[#A2FF00]" />
                        <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>Sample Pricing</span>
                      </div>
                      <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
                        Pricing options will be populated from selected products when creating a proposal.
                      </p>
                    </div>
                  </div>
                ) : currentSection.editor_mode === 'visual' && currentSection.visual_content ? (
                  /* Visual Editor Content */
                  <BuilderContentRenderer content={currentSection.visual_content} isDark={isDark} />
                ) : (
                  <div 
                    className={`prose max-w-none prose-headings:font-unbounded prose-h2:text-xl prose-h3:text-lg template-content ${isDark ? 'prose-invert' : 'prose-gray light-mode-content'}`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(fixImageUrls(replacePlaceholders(currentSection.content))) }}
                  />
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                disabled={activeSection === 0}
                className={`px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'} border rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Previous
              </button>
              
              {activeSection < (template.sections?.length || 0) - 1 ? (
                <button
                  onClick={() => setActiveSection(activeSection + 1)}
                  className="px-4 py-2 bg-[#A2FF00] text-black font-bold rounded-sm hover:bg-[#8EFF00] transition-colors"
                >
                  Next
                </button>
              ) : (
                <span className={`px-4 py-2 ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'} rounded-sm`}>
                  End of Template
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content Styling to match editor */}
      <style>{`
        /* TEMPLATE CONTENT SPACING */
        .template-content {
          white-space: normal;
          word-spacing: normal;
          letter-spacing: normal;
        }
        .template-content p {
          margin-bottom: 1em;
          line-height: 1.7;
        }
        .template-content p:last-child {
          margin-bottom: 0;
        }
        .template-content h1, 
        .template-content h2, 
        .template-content h3 {
          margin-top: 1.5em;
          margin-bottom: 0.75em;
        }
        .template-content h1:first-child, 
        .template-content h2:first-child, 
        .template-content h3:first-child {
          margin-top: 0;
        }
        .template-content ul, 
        .template-content ol {
          margin-bottom: 1em;
          padding-left: 1.5em;
        }
        .template-content ul {
          list-style-type: disc;
        }
        .template-content ol {
          list-style-type: decimal;
        }
        .template-content li {
          margin-bottom: 0.5em;
          line-height: 1.6;
          display: list-item;
        }
        .template-content blockquote {
          margin: 1em 0;
          padding-left: 1em;
          border-left: 3px solid #A2FF00;
        }
        
        /* PROSE LIST STYLES */
        .prose ul {
          list-style-type: disc;
          padding-left: 1.5em;
        }
        .prose ol {
          list-style-type: decimal;
          padding-left: 1.5em;
        }
        .prose li {
          display: list-item;
        }
        
        /* TEXT ALIGNMENT - Preserve TipTap alignment styles */
        .prose [style*="text-align: center"],
        .prose [style*="text-align:center"] {
          text-align: center !important;
        }
        .prose [style*="text-align: right"],
        .prose [style*="text-align:right"] {
          text-align: right !important;
        }
        .prose [style*="text-align: justify"],
        .prose [style*="text-align:justify"] {
          text-align: justify !important;
        }
        
        /* COLUMN LAYOUTS - Preserve grid layouts */
        .prose .column-layout,
        .prose [style*="grid-template-columns"] {
          display: grid !important;
          gap: 1rem;
        }
        .prose .column {
          padding: 1rem;
          min-height: 100px;
          border: 1px dashed rgba(0,0,0,0.2);
          border-radius: 4px;
        }
        
        /* VIDEO EMBEDS */
        .prose .video-embed {
          position: relative !important;
          padding-bottom: 56.25% !important;
          height: 0 !important;
          overflow: hidden !important;
          margin: 1rem 0 !important;
        }
        .prose .video-embed iframe {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
        }
        
        /* TABLES */
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        .prose th, .prose td {
          border: 1px solid rgba(0,0,0,0.15);
          padding: 0.75rem;
          text-align: left;
        }
        .prose th {
          background: rgba(0,0,0,0.05);
          font-weight: bold;
        }
        
        /* IMAGES */
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }
        
        /* HORIZONTAL RULE */
        .prose hr {
          border: none;
          border-top: 1px solid rgba(0,0,0,0.15);
          margin: 2rem 0;
        }
        
        /* LIGHT MODE CONTENT - Force readable text colors */
        .light-mode-content {
          color: #374151 !important;
        }
        .light-mode-content p {
          color: #374151 !important;
        }
        .light-mode-content h1, 
        .light-mode-content h2, 
        .light-mode-content h3, 
        .light-mode-content h4 {
          color: #111827 !important;
        }
        .light-mode-content li {
          color: #374151 !important;
        }
        .light-mode-content strong {
          color: #16a34a !important;
        }
        .light-mode-content a {
          color: #16a34a !important;
        }
        .light-mode-content blockquote {
          color: #6b7280 !important;
          border-left-color: #16a34a !important;
        }
        .light-mode-content code {
          color: #16a34a !important;
          background: #f3f4f6 !important;
        }
        .light-mode-content pre {
          background: #f3f4f6 !important;
        }
        .light-mode-content pre code {
          color: #374151 !important;
        }
        
        /* Override any inline white/light colors in light mode */
        .light-mode-content [style*="color: rgb(255"],
        .light-mode-content [style*="color:rgb(255"],
        .light-mode-content [style*="color: #fff"],
        .light-mode-content [style*="color:#fff"],
        .light-mode-content [style*="color: white"],
        .light-mode-content [style*="color:white"],
        .light-mode-content [style*="color: #FFF"],
        .light-mode-content [style*="color:#FFF"],
        .light-mode-content [style*="color: #FFFFFF"],
        .light-mode-content [style*="color:#FFFFFF"] {
          color: #374151 !important;
        }
        
        /* Handle spans with white text in light mode */
        .light-mode-content span[style*="color: rgb(255, 255, 255)"],
        .light-mode-content span[style*="color:rgb(255,255,255)"] {
          color: #374151 !important;
        }
        
        /* Ensure table cells have proper text color in light mode */
        .light-mode-content td {
          color: #374151 !important;
        }
        .light-mode-content th {
          color: #111827 !important;
        }
      `}</style>
    </div>
  );
};

export default TemplatePreview;
